/**
 * Jira Integration API Service
 *
 * Service functions for interacting with the Jira integration API endpoints.
 * Follows the same pattern as logfireService.ts.
 */

import { apiClient } from "./api";
import type {
  JiraIntegrationRead,
  JiraIntegrationCreate,
  JiraIntegrationUpdate,
  JiraConnectionTest,
  JiraConnectionTestRequest,
} from "@/types/jiraIntegration";

const API_PREFIX = "/api/v1/integrations/jira";

/**
 * Get the current organization's Jira integration.
 * Returns the integration or throws (404 if not configured).
 */
export async function getJiraIntegration(): Promise<JiraIntegrationRead> {
  return apiClient.get<JiraIntegrationRead>(API_PREFIX);
}

/**
 * Create a new Jira integration for the current organization.
 */
export async function createJiraIntegration(
  data: JiraIntegrationCreate
): Promise<JiraIntegrationRead> {
  return apiClient.post<JiraIntegrationRead>(API_PREFIX, data);
}

/**
 * Update the current organization's Jira integration.
 */
export async function updateJiraIntegration(
  data: JiraIntegrationUpdate
): Promise<JiraIntegrationRead> {
  return apiClient.patch<JiraIntegrationRead>(API_PREFIX, data);
}

/**
 * Delete the current organization's Jira integration.
 */
export async function deleteJiraIntegration(): Promise<void> {
  return apiClient.delete(API_PREFIX);
}

/**
 * Fetch Jira projects accessible to the configured integration.
 * Requires a saved Jira integration.
 */
export async function getJiraProjects(): Promise<
  { key: string; name: string }[]
> {
  return apiClient.get<{ key: string; name: string }[]>(
    `${API_PREFIX}/projects`
  );
}

/**
 * Fetch issue types available for a Jira project.
 * Requires a saved Jira integration.
 */
export async function getJiraIssueTypes(
  projectKey: string
): Promise<{ id: string; name: string }[]> {
  return apiClient.get<{ id: string; name: string }[]>(
    `${API_PREFIX}/issue-types?project_key=${encodeURIComponent(projectKey)}`
  );
}

/**
 * Test the Jira connection.
 *
 * - With `credentials`: tests inline credentials (pre-save testing).
 * - Without `credentials`: tests stored credentials from the DB.
 * Optionally pass a project key to validate project access.
 */
export async function testJiraConnection(
  projectKey?: string,
  credentials?: JiraConnectionTestRequest
): Promise<JiraConnectionTest> {
  const params = new URLSearchParams();
  if (projectKey) {
    params.append("project_key", projectKey);
  }
  const queryString = params.toString();
  const url = queryString ? `${API_PREFIX}/test?${queryString}` : `${API_PREFIX}/test`;
  return apiClient.post<JiraConnectionTest>(url, credentials);
}
